import { useCallback, useEffect, useMemo, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import styles from './App.module.css';
import './index.css';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const STEP_PROVIDERS = 'providers';
const STEP_SCHEDULE = 'schedule';
const STEP_CONTACT = 'contact';
const STEP_DETAILS = 'details';
const STEP_CONFIRMATION = 'confirmation';

const BOOKING_FLOW_LABELS = ['Contact', 'Questions', 'Schedule', 'Confirmed'];

const DEFAULT_SESSION_MINUTES = 45;

function getBookingFlowIndex(step) {
  switch (step) {
    case STEP_CONTACT:
      return 0;
    case STEP_DETAILS:
      return 1;
    case STEP_PROVIDERS:
    case STEP_SCHEDULE:
      return 2;
    case STEP_CONFIRMATION:
      return 3;
    default:
      return 0;
  }
}

function isValidBookingEmail(value) {
  const v = String(value || '').trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function hasBookingPhoneDigits(value) {
  return /\d/.test(String(value || ''));
}

function advisorCopy(firstName) {
  return {
    role: 'Admissions Advisor',
    headline: 'A dedicated conversation to help you understand the residency and what comes next.',
    description: `Book a 45-minute Google Meet with ${firstName} to get a realistic picture of the program, culture, and how the residency supports your goals.`,
    expertise: ['Residency overview', 'Program fit'],
    sidebarQuote:
      '"Come with your questions. You should leave with a clear picture of the residency and your next step."',
    testimonial: '"The call felt personal, practical, and easy to act on afterward."',
    progressLabel: 'Session format',
    progressValue: 100,
    sessionsCompleted: '45-minute Google Meet · Admissions Advisor Booking',
    sessionType: 'Residency insight',
    quickTips: [
      'Write down 2–3 questions about the residency, schedule, or expectations.',
      'Find a quiet spot and test your camera and mic a few minutes before the call.',
      'Ask what a typical week looks like and how admissions advisors support residents.',
    ],
  };
}

const PROVIDER_COPY = {
  justin: advisorCopy('Justin'),
  deshon: advisorCopy('Deshon'),
  donte: advisorCopy('Donte'),
  ivey: advisorCopy('Ivey'),
  rohith: advisorCopy('Rohith'),
  default: {
    role: 'Admissions Advisor',
    headline: 'A dedicated admissions advisor conversation for prospective residency candidates.',
    description:
      'Book a short call to learn how the residency works and whether it aligns with what you are looking for.',
    expertise: ['Residency', 'admissions advisor support'],
    sidebarQuote:
      '"Every call is about your questions and getting clear, practical insight."',
    testimonial:
      '"Simple to book, and I got real answers from someone who knows the program."',
    progressLabel: 'Session format',
    progressValue: 100,
    sessionsCompleted: '45-minute Google Meet · Admissions Advisor Booking',
    sessionType: 'Residency insight',
    quickTips: [
      'List what you hope to learn about the residency.',
      'Use a strong internet connection and join from a laptop if you can.',
      'Ask how admissions advisors stay involved after you join the program.',
    ],
  },
};

const PROVIDER_IMAGES = {
  justin: '/justin-advisor.jpg',
};

function providerPhoto(provider) {
  if (!provider?.id) return '';
  return PROVIDER_IMAGES[provider.id] || '';
}

function providerFirstName(provider) {
  const name = String(provider?.name || '').trim();
  if (!name) return 'your admissions advisor';
  return name.split(/\s+/)[0];
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const DEFAULT_CAMPAIGNS = {
  livestream: 'TRADEIT_DAY_LIVESTREAM_OCT_15_2026',
  youtube_replay: 'TRADEIT_DAY_YOUTUBE_REPLAY_OCT_15_2026',
};

function parseUtmFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const utm = {};
  for (const key of UTM_KEYS) {
    const value = String(params.get(key) || '').trim();
    if (value) utm[key] = value;
  }
  const source = String(params.get('source') || '').trim();
  if (source && !utm.utm_source) utm.utm_source = source;
  const campaign = String(params.get('campaign') || params.get('event') || '').trim();
  if (campaign && !utm.utm_campaign) utm.utm_campaign = campaign;
  const sourceId = String(utm.utm_source || '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const mappedSource =
    sourceId === 'live' || sourceId === 'live_stream'
      ? 'livestream'
      : sourceId === 'youtube' || sourceId === 'replay'
        ? 'youtube_replay'
        : sourceId;
  if (!utm.utm_campaign && DEFAULT_CAMPAIGNS[mappedSource]) {
    utm.utm_campaign = DEFAULT_CAMPAIGNS[mappedSource];
  }
  return utm;
}

function timezoneDisplay(timezone) {
  const zone = timezone || 'America/New_York';
  if (zone === 'America/New_York') {
    return {
      title: 'All times are Eastern (America/New_York)',
      badge: `Eastern (EST/EDT) · ${zone}`,
      confirm: 'Eastern (EST/EDT)',
    };
  }
  if (zone === 'America/Chicago') {
    return {
      title: 'All times are Central (America/Chicago)',
      badge: `Central (CST/CDT) · ${zone}`,
      confirm: 'Central (CST/CDT)',
    };
  }
  return {
    title: `All times are ${zone}`,
    badge: zone,
    confirm: zone,
  };
}

const INTAKE_QUESTIONS = [
  {
    key: 'goal',
    type: 'choice',
    shortLabel: 'Goal',
    label: 'What are you hoping to improve with your trading right now?',
    options: [
      'I am new and need a clear place to start.',
      'I have learned a lot but still do not have a clear process.',
      'I struggle with consistency, risk, or decision-making.',
      'I want more feedback while I practice.',
      'I want to know whether this type of support fits my goals.',
    ],
  },
  {
    key: 'investment',
    type: 'choice',
    shortLabel: 'Investment readiness',
    label:
      'The current Residency options are Platinum at $8,000 and Diamond at $12,000. If the program makes sense for you, which best describes your situation?',
    options: [
      'I can consider the investment now.',
      'I may be able to consider it, but I need to understand the fit first.',
      'I would need an approved payment option.',
      'I cannot currently consider an $8,000–$12,000 investment.',
    ],
  },
  {
    key: 'nextStep',
    type: 'choice',
    shortLabel: 'Preferred next step',
    label: 'If the program makes sense for you, which conversation would be most helpful?',
    options: [
      'A 45-minute Admissions Call to discuss fit and enrollment.',
      'A 15-minute Triage Call to clarify fit and next steps.',
      'I need to plan for the investment before choosing a call.',
    ],
  },
];

const EMPTY_INTAKE = INTAKE_QUESTIONS.reduce((acc, question) => {
  acc[question.key] = '';
  return acc;
}, {});

function isIntakeAnswerComplete(question, value) {
  const trimmed = String(value || '').trim();
  if (question.type === 'text') {
    return trimmed.length >= (question.minLength || 1);
  }
  return Boolean(trimmed);
}

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

function todayYmd() {
  const now = new Date();
  return toYmd(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12));
}

function toYmd(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fromYmd(ymd) {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
}

function shiftMonth(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1, 12);
}

function isUnavailableBookingDate() {
  return false;
}

function nextBookableYmd(ymd) {
  let current = fromYmd(ymd);
  while (isUnavailableBookingDate(current)) {
    current = new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1, 12);
  }
  return toYmd(current);
}

function getInitials(name) {
  return (name || 'SSC')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function getProfile(provider) {
  if (!provider) return PROVIDER_COPY.default;
  return PROVIDER_COPY[provider.id] || PROVIDER_COPY.default;
}

function formatDate(ymd, options) {
  return new Intl.DateTimeFormat('en-US', options).format(fromYmd(ymd));
}

function formatDateWithZone(iso, timezone, options) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || undefined,
    ...options,
  }).format(new Date(iso));
}

function formatTimeRange(startIso, endIso, timezone) {
  const start = formatDateWithZone(startIso, timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const end = formatDateWithZone(endIso, timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${start} — ${end}`;
}

function buildCalendarDays(monthDate, selectedDate, minDate) {
  const startOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1, 12);
  const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 12);
  const firstWeekDay = startOfMonth.getDay();
  const totalCells = Math.ceil((firstWeekDay + endOfMonth.getDate()) / 7) * 7;
  const startGrid = new Date(startOfMonth);
  startGrid.setDate(startGrid.getDate() - firstWeekDay);

  return Array.from({ length: totalCells }, (_, index) => {
    const current = new Date(startGrid);
    current.setDate(startGrid.getDate() + index);
    const ymd = toYmd(current);
    const inMonth = current.getMonth() === monthDate.getMonth();
    return {
      key: ymd,
      ymd,
      label: current.getDate(),
      inMonth,
      disabled: ymd < minDate || isUnavailableBookingDate(current),
      isSelected: ymd === selectedDate,
      isToday: ymd === todayYmd(),
    };
  });
}

function groupSlots(slots) {
  const groups = {
    Morning: [],
    Afternoon: [],
    Evening: [],
  };

  slots.forEach((slot) => {
    const label = slot.label.toUpperCase();
    const hour = parseInt(label, 10);
    if (label.includes('AM')) {
      groups.Morning.push(slot);
      return;
    }
    if (hour < 5) {
      groups.Afternoon.push(slot);
      return;
    }
    groups.Evening.push(slot);
  });

  return Object.entries(groups).filter(([, list]) => list.length > 0);
}

function googleCalendarTemplateUrl({ title, startIso, endIso, details }) {
  const toGCalUtc = (iso) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
  };

  const start = toGCalUtc(startIso);
  const end = toGCalUtc(endIso);
  if (!start || !end) return '';

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${start}/${end}`,
  });
  if (details) params.set('details', details);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function escapeIcsText(value) {
  return String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function createIcsHref(booking) {
  if (!booking?.startIso || !booking?.endIso) return '';
  const toUtc = (iso) => new Date(iso).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  const content = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Admissions Advisor Booking//EN',
    'BEGIN:VEVENT',
    `UID:${Date.now()}@ssa-booking`,
    `DTSTAMP:${toUtc(new Date().toISOString())}`,
    `DTSTART:${toUtc(booking.startIso)}`,
    `DTEND:${toUtc(booking.endIso)}`,
    `SUMMARY:${escapeIcsText(booking.sessionTitle)}`,
    `DESCRIPTION:${escapeIcsText(`Google Meet: ${booking.meetLink || ''}`)}`,
    `LOCATION:${escapeIcsText('Virtual Video Call')}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return `data:text/calendar;charset=utf-8,${encodeURIComponent(content)}`;
}

function Icon({ name, filled = false, className }) {
  return (
    <span
      className={cx(styles.icon, className)}
      style={filled ? { fontVariationSettings: "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24" } : undefined}
      aria-hidden="true"
    >
      {name}
    </span>
  );
}

function Avatar({ label, large = false, square = false, src }) {
  return (
    <div
      className={cx(
        square ? styles.avatarSquare : styles.avatarCircle,
        large ? styles.avatarLarge : '',
        square ? styles.avatarSquareGradient : styles.avatarCircleGradient
      )}
      aria-hidden="true"
    >
      {src ? <img src={src} alt={label} className={styles.avatarImage} /> : <span>{getInitials(label)}</span>}
    </div>
  );
}

function TeamAvatars({ people, large = false, square = false }) {
  if (!people?.length) {
    return <Avatar label="Admissions Advisor" large={large} square={square} />;
  }
  if (people.length === 1) {
    return <Avatar label={people[0].name} src={providerPhoto(people[0])} large={large} square={square} />;
  }
  return (
    <div className={styles.teamAvatars} aria-hidden="true">
      {people.map((person) => (
        <Avatar key={person.id} label={person.name} src={providerPhoto(person)} large={large} square={square} />
      ))}
    </div>
  );
}

function App() {
  const minDate = useMemo(() => nextBookableYmd(todayYmd()), []);
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [dateStr, setDateStr] = useState(minDate);
  const [visibleMonth, setVisibleMonth] = useState(fromYmd(minDate));
  const [timezone, setTimezone] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotStart, setSlotStart] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [intakeAnswers, setIntakeAnswers] = useState(EMPTY_INTAKE);
  const [intakeStep, setIntakeStep] = useState(0);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [currentStep, setCurrentStep] = useState(STEP_CONTACT);
  const [lastBooking, setLastBooking] = useState(null);
  const [copiedMeet, setCopiedMeet] = useState(false);
  const [rescheduleToken, setRescheduleToken] = useState('');
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [sessionMinutes, setSessionMinutes] = useState(DEFAULT_SESSION_MINUTES);
  const [bufferMinutes, setBufferMinutes] = useState(15);
  const [manageToken, setManageToken] = useState('');
  const [utm, setUtm] = useState({});
  const [qualification, setQualification] = useState(null);
  const [societyUrl, setSocietyUrl] = useState('https://www.tradeitsolutions.com');

  useEffect(() => {
    let cancelled = false;

    async function loadProviders() {
      setProvidersLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/api/booking/providers`);
        const data = await res.json();
        if (cancelled) return;

        if (!data.success || !Array.isArray(data.providers)) {
          setProviders([]);
          setError(data.message || 'Could not load Admissions Advisors.');
          return;
        }

        setProviders(data.providers);
        const configRes = await fetch(`${API_URL}/api/booking/config`);
        const config = await configRes.json();
        if (!cancelled && config?.success) {
          if (config.durationMinutes) setSessionMinutes(config.durationMinutes);
          if (config.bufferMinutes != null) setBufferMinutes(config.bufferMinutes);
          if (config.societyUrl) setSocietyUrl(config.societyUrl);
          if (config.timezone) setTimezone(config.timezone);
        }
      } catch (err) {
        if (!cancelled) {
          setProviders([]);
          setError('Network error loading admissions advisors.');
        }
      } finally {
        if (!cancelled) setProvidersLoading(false);
      }
    }

    loadProviders();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setVisibleMonth(fromYmd(dateStr));
  }, [dateStr]);

  useEffect(() => {
    if (isUnavailableBookingDate(fromYmd(dateStr))) {
      setDateStr(nextBookableYmd(dateStr));
    }
  }, [dateStr]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const parsedUtm = parseUtmFromSearch(window.location.search);
    if (Object.keys(parsedUtm).length) setUtm(parsedUtm);
    const token = params.get('reschedule');
    if (!token) return;

    let cancelled = false;
    async function loadRescheduleBooking() {
      setRescheduleLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_URL}/api/booking/reschedule?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || !data.success || !data.booking) {
          setError(data.message || 'This reschedule link is no longer valid.');
          return;
        }

        const booking = data.booking;
        const nextDate = booking.startIso
          ? nextBookableYmd(toYmd(new Date(booking.startIso)))
          : minDate;

        setRescheduleToken(token);
        setRescheduleBooking(booking);
        setName(booking.customerName || '');
        setEmail(booking.customerEmail || '');
        setPhone(booking.customerPhone || '');
        setDescription(booking.bookingDescription || '');
        setTimezone(booking.timezone || '');
        setDateStr(nextDate);
        setCurrentStep(STEP_SCHEDULE);
      } catch (err) {
        if (!cancelled) {
          setError('Could not load the booking to reschedule.');
        }
      } finally {
        if (!cancelled) setRescheduleLoading(false);
      }
    }

    loadRescheduleBooking();
    return () => {
      cancelled = true;
    };
  }, [minDate]);

  const selectedSlotEnd = useMemo(() => {
    if (!slotStart) return '';
    return new Date(
      new Date(slotStart).getTime() + sessionMinutes * 60 * 1000
    ).toISOString();
  }, [sessionMinutes, slotStart]);

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);
    setError('');
    setSlotStart('');

    try {
      const query = new URLSearchParams({
        date: dateStr,
        durationMinutes: String(sessionMinutes),
      });
      const providerId = rescheduleBooking?.provider?.id;
      if (providerId) {
        query.set('providerId', providerId);
      }
      const res = await fetch(`${API_URL}/api/booking/slots?${query}`);
      const data = await res.json();

      if (!data.success) {
        setSlots([]);
        setError(data.message || 'Could not load times.');
        return;
      }

      setSlots(data.slots || []);
      setTimezone(data.timezone || '');
    } catch (err) {
      setSlots([]);
      setError('Network error loading availability.');
    } finally {
      setLoadingSlots(false);
    }
  }, [dateStr, rescheduleBooking, sessionMinutes]);

  useEffect(() => {
    if (currentStep === STEP_SCHEDULE) {
      loadSlots();
    }
  }, [currentStep, loadSlots]);

  const canContinueContact = useMemo(
    () => Boolean(name.trim() && isValidBookingEmail(email) && hasBookingPhoneDigits(phone)),
    [email, name, phone]
  );

  const isRescheduleMode = Boolean(rescheduleToken && rescheduleBooking);

  const intakeComplete = useMemo(
    () =>
      INTAKE_QUESTIONS.every((question) =>
        isIntakeAnswerComplete(question, intakeAnswers[question.key])
      ),
    [intakeAnswers]
  );
  const currentIntakeQuestion = INTAKE_QUESTIONS[intakeStep] || INTAKE_QUESTIONS[0];
  const currentIntakeValue = intakeAnswers[currentIntakeQuestion.key] || '';
  const currentIntakeComplete = isIntakeAnswerComplete(
    currentIntakeQuestion,
    currentIntakeValue
  );
  const isLastIntakeStep = intakeStep === INTAKE_QUESTIONS.length - 1;

  const canBookSlot = useMemo(
    () =>
      Boolean(
        slotStart &&
          canContinueContact &&
          (isRescheduleMode || !TURNSTILE_SITE_KEY || turnstileToken)
      ),
    [canContinueContact, isRescheduleMode, slotStart, turnstileToken]
  );
  const calendarDays = useMemo(
    () => buildCalendarDays(visibleMonth, dateStr, minDate),
    [visibleMonth, dateStr, minDate]
  );

  const slotGroups = useMemo(() => groupSlots(slots), [slots]);

  const previewSummary = useMemo(() => {
    if (!slotStart || !selectedSlotEnd) return '';
    return `${formatDate(dateStr, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    })} — ${formatTimeRange(slotStart, selectedSlotEnd, timezone)} · ${sessionMinutes} min admissions advisor call`;
  }, [dateStr, selectedSlotEnd, sessionMinutes, slotStart, timezone]);

  const bookingTitle = useMemo(
    () => (sessionMinutes === 15 ? 'Admissions Triage Call' : 'Admissions Advisor Booking'),
    [sessionMinutes]
  );

  const qualifyPayload = useMemo(
    () => ({
      email: email.trim(),
      name: name.trim(),
      phone: phone.trim(),
      utm,
      source: utm.utm_source || '',
      campaignName: utm.utm_campaign || '',
      answers: {
        goal: intakeAnswers.goal || '',
        investment: intakeAnswers.investment || '',
        nextStep: intakeAnswers.nextStep || '',
      },
    }),
    [email, intakeAnswers, name, phone, utm]
  );

  const calendarAddUrl = useMemo(() => {
    if (!lastBooking) return '';
    return googleCalendarTemplateUrl({
      title: lastBooking.sessionTitle,
      startIso: lastBooking.startIso,
      endIso: lastBooking.endIso,
      details: `Admissions Advisor Booking — residency insight call. Google Meet: ${lastBooking.meetLink}`,
    });
  }, [lastBooking]);

  const icsHref = useMemo(() => createIcsHref(lastBooking), [lastBooking]);

  function applyBookingResult(data, options = {}) {
    const providerProfile = getProfile({ id: data.providerId || '' });
    const nextManageToken = data.manageToken || manageToken || rescheduleToken;
    setManageToken(nextManageToken);
    setLastBooking({
      meetLink: data.meetLink,
      startIso: data.startIso,
      endIso: data.endIso,
      durationMinutes: data.durationMinutes || sessionMinutes,
      providerName: data.providerName || 'Your admissions advisor',
      providerId: data.providerId || '',
      providerRole: providerProfile.role,
      sessionType: providerProfile.sessionType,
      sessionTitle: bookingTitle,
      timezone,
      quickTips: providerProfile.quickTips,
      wasRescheduled: Boolean(options.wasRescheduled),
      rescheduleUrl: data.rescheduleUrl || '',
    });
    if (data.durationMinutes) setSessionMinutes(data.durationMinutes);
  }

  async function handleBookSlot() {
    if (!canBookSlot) return;

    setError('');
    setSubmitLoading(true);

    try {
      const endpoint = isRescheduleMode
        ? `${API_URL}/api/booking/reschedule`
        : `${API_URL}/api/booking/create`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(isRescheduleMode ? { token: rescheduleToken } : {}),
          durationMinutes: sessionMinutes,
          slotStart,
          email: email.trim(),
          name: name.trim(),
          phone: phone.trim(),
          description: isRescheduleMode ? description.trim() : '',
          ...qualifyPayload,
          intake: INTAKE_QUESTIONS.map((question) => ({
            label: question.shortLabel,
            value: String(intakeAnswers[question.key] || '').trim(),
          })).filter((item) => item.value),
          ...(TURNSTILE_SITE_KEY && turnstileToken ? { turnstileToken } : {}),
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Booking could not be completed.');
        setTurnstileToken('');
        return;
      }

      applyBookingResult(data, { wasRescheduled: isRescheduleMode });
      setCopiedMeet(false);
      setTurnstileToken('');
      setSlotStart('');
      if (isRescheduleMode) {
        setRescheduleToken(data.manageToken || rescheduleToken);
      }
      setCurrentStep(STEP_CONFIRMATION);
      loadSlots();
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  }

  async function handleSaveIntake(event) {
    event.preventDefault();
    if (isRescheduleMode) {
      setCurrentStep(STEP_CONFIRMATION);
      return;
    }
    if (!isLastIntakeStep) {
      goToNextIntakeStep();
      return;
    }
    if (!intakeComplete) return;

    setError('');
    setSubmitLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/booking/qualify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(qualifyPayload),
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.qualification) {
        setError(data.message || 'Answers could not be saved.');
        return;
      }
      setQualification(data.qualification);
      if (data.qualification.durationMinutes) {
        setSessionMinutes(data.qualification.durationMinutes);
      }
      if (data.qualification.calendar) {
        setCurrentStep(STEP_SCHEDULE);
      } else {
        setCurrentStep(STEP_CONFIRMATION);
      }
    } catch (err) {
      setError('Answers could not be saved. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  }

  async function saveContactAndContinue() {
    if (!canContinueContact) return;
    setError('');
    setSubmitLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/booking/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          utm,
          source: utm.utm_source || '',
          campaignName: utm.utm_campaign || '',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not save your contact details.');
        return;
      }
      setIntakeStep(0);
      setCurrentStep(STEP_DETAILS);
    } catch (err) {
      setError('Could not save your contact details. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  }

  function requireContactInfo() {
    if (canContinueContact) return true;
    setError('Please fill in your name, email, and phone before continuing.');
    setCurrentStep(STEP_CONTACT);
    return false;
  }

  function goToSchedule() {
    if (!requireContactInfo()) return;
    if (!isRescheduleMode && !qualification?.calendar) {
      setError('Answer the three questions first so we can send you to the right call.');
      setCurrentStep(STEP_DETAILS);
      return;
    }
    setError('');
    setCurrentStep(STEP_SCHEDULE);
  }

  function goFromScheduleToReview() {
    handleBookSlot();
  }

  function goToPreviousIntakeStep() {
    if (intakeStep === 0) {
      setCurrentStep(STEP_CONTACT);
      return;
    }
    setIntakeStep((current) => Math.max(0, current - 1));
  }

  function goToNextIntakeStep() {
    if (!currentIntakeComplete || isLastIntakeStep) return;
    setIntakeStep((current) => Math.min(INTAKE_QUESTIONS.length - 1, current + 1));
  }

  function selectIntakeOption(question, option) {
    setIntakeAnswers((current) => ({ ...current, [question.key]: option }));
  }

  function goToScheduleFromNav() {
    goToSchedule();
  }

  function goToProvidersFromContact() {
    saveContactAndContinue();
  }

  function resetToProviders() {
    if (!requireContactInfo()) return;
    setError('');
    setCurrentStep(STEP_DETAILS);
  }

  function resetToLanding() {
    setRescheduleToken('');
    setRescheduleBooking(null);
    setCurrentStep(STEP_CONTACT);
    setError('');
  }

  function goToSessions() {
    if (lastBooking) {
      setCurrentStep(STEP_CONFIRMATION);
      return;
    }
    if (!requireContactInfo()) return;
    setError('');
    setCurrentStep(isRescheduleMode || qualification?.calendar ? STEP_SCHEDULE : STEP_DETAILS);
  }

  async function copyMeetLink() {
    if (!lastBooking?.meetLink) return;
    try {
      await navigator.clipboard.writeText(lastBooking.meetLink);
      setCopiedMeet(true);
      setTimeout(() => setCopiedMeet(false), 2000);
    } catch (err) {
      setError('Could not copy the meeting link.');
    }
  }

  const advisorsNavActive =
    currentStep === STEP_PROVIDERS ||
    currentStep === STEP_SCHEDULE ||
    currentStep === STEP_DETAILS;
  const bookingNavActive = currentStep === STEP_CONFIRMATION;
  const flowIndex = getBookingFlowIndex(currentStep);
  const assignedProvider = rescheduleBooking?.provider || null;
  const assignedFirstName = providerFirstName(assignedProvider);

  return (
    <div className={styles.appShell}>
      <header className={styles.topbar}>
        <div className={styles.topbarInner}>
          <button type="button" className={styles.brand} onClick={resetToLanding} aria-label="TradeIT Admissions Advisor Booking">
            <img
              src="/tradeit-crest.png"
              alt="TradeIT"
              className={styles.brandLogo}
            />
            <span className={styles.brandCopy}>
              <span className={styles.brandKicker}>Office of Admissions</span>
              <span className={styles.brandName}>Admissions Advisor Booking</span>
            </span>
          </button>

          <nav className={styles.desktopNav} aria-label="Primary">
            <button
              type="button"
              className={cx(styles.navLink, advisorsNavActive && styles.navLinkActive)}
              onClick={resetToProviders}
            >
              Questions
            </button>
            <button
              type="button"
              className={cx(styles.navLink, bookingNavActive && styles.navLinkActive)}
              onClick={goToSessions}
            >
              My Booking
            </button>
          </nav>
        </div>
      </header>

      <main className={styles.mainShell}>
        {error ? (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        ) : null}
        {rescheduleLoading && !rescheduleBooking ? (
          <div className={styles.emptyCard}>Loading booking details…</div>
        ) : null}

        <nav className={styles.stepIndicator} aria-label="Booking progress">
          <ol className={styles.stepIndicatorList}>
            {BOOKING_FLOW_LABELS.map((label, i) => {
              const done = i < flowIndex;
              const active = i === flowIndex;
              return (
                <li
                  key={label}
                  className={cx(
                    styles.stepIndicatorStep,
                    done && styles.stepIndicatorStepDone,
                    active && styles.stepIndicatorStepActive
                  )}
                  aria-current={active ? 'step' : undefined}
                >
                  <span className={styles.stepIndicatorDot} aria-hidden="true">
                    {done ? (
                      <Icon name="check" className={styles.stepIndicatorCheck} />
                    ) : (
                      <span className={styles.stepIndicatorNum}>{i + 1}</span>
                    )}
                  </span>
                  <span className={styles.stepIndicatorLabel}>{label}</span>
                </li>
              );
            })}
          </ol>
        </nav>

        {currentStep === STEP_PROVIDERS ? (
          <section className={styles.providersPage}>
            <div className={styles.providersContent}>
              <header className={styles.heroHeader}>
                <button type="button" className={styles.backToContact} onClick={() => setCurrentStep(STEP_CONTACT)}>
                  <Icon name="arrow_back" className={styles.backToContactIcon} />
                  Back to contact info
                </button>
                <span className={styles.sectionKicker}>Admissions Advisors</span>
                <h1>Meet the team</h1>
                <p>
                  These are the Admissions Advisors who take residency insight calls. You
                  don’t choose one—we’ll assign an available admissions advisor when you book so calls stay
                  evenly shared across the team.
                </p>
              </header>

              {providersLoading ? (
                <div className={styles.emptyCard}>Loading admissions advisors…</div>
              ) : providers.length === 0 ? (
                <div className={styles.emptyCard}>No admissions advisors are available to book right now.</div>
              ) : (
                <>
                  <div className={styles.providerGrid}>
                    {providers.map((provider) => {
                      const profile = getProfile(provider);
                      const photo = providerPhoto(provider);
                      return (
                        <article key={provider.id} className={styles.providerCard}>
                          <div className={styles.providerCardInner}>
                            <div className={styles.providerCardPhoto}>
                              {photo ? (
                                <img src={photo} alt="" />
                              ) : (
                                <Avatar label={provider.name} large square />
                              )}
                            </div>
                            <div className={styles.providerCardBody}>
                              <p className={styles.providerRole}>{profile.role}</p>
                              <h3>{provider.name}</h3>
                              <p className={styles.providerDescription}>{profile.headline}</p>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  <div className={styles.providerActions}>
                    <button
                      type="button"
                      className={styles.ctaButton}
                      onClick={goToSchedule}
                      disabled={providers.length === 0}
                    >
                      <span>Continue to availability</span>
                      <Icon name="arrow_forward" className={styles.inlineIcon} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>
        ) : null}

        {currentStep === STEP_SCHEDULE ? (
          <section className={styles.schedulePage}>
            <aside className={styles.contextSidebar}>
              <div className={styles.contextCard}>
                <div className={styles.contextAvatarWrap}>
                  {isRescheduleMode ? (
                    <Avatar
                      label={assignedProvider?.name || 'Admissions Advisor'}
                      large
                      src={providerPhoto(assignedProvider)}
                    />
                  ) : (
                    <TeamAvatars people={providers} large />
                  )}
                </div>
                <h2>
                  {isRescheduleMode
                    ? 'Reschedule your booking'
                    : 'Your Admissions Advisor'}
                </h2>
                <p className={styles.contextRole}>
                  {isRescheduleMode
                    ? `Your booking stays with ${rescheduleBooking?.provider?.name || 'your assigned admissions advisor'}`
                    : 'Matched automatically when you pick a time'}
                </p>
                <div className={styles.tagRow}>
                  <span className={styles.tag}>
                    {isRescheduleMode ? 'Same assigned admissions advisor' : 'Auto-assigned'}
                  </span>
                  <span className={styles.tag}>{sessionMinutes}-minute session</span>
                  <span className={styles.tag}>Google Meet</span>
                </div>
                <p className={styles.contextQuote}>
                  {isRescheduleMode
                    ? 'Choose a new time and we will update your existing booking without creating a duplicate meeting.'
                    : 'We’ll match you with an available Admissions Advisor when you book so calls stay evenly shared across the team.'}
                </p>
              </div>

              <div className={styles.progressCard}>
                <h3>What to expect</h3>
                <ul className={styles.expectList}>
                  <li>{sessionMinutes} minutes on Google Meet, plus a buffer before the next call</li>
                  <li>Honest picture of the residency</li>
                  <li>Time to ask about fit, schedule, and support</li>
                </ul>
              </div>
            </aside>

            <div className={styles.scheduleContent}>
              <div className={styles.sectionHeaderRow}>
                <div>
                  <span className={styles.sectionKicker}>
                    {sessionMinutes === 15 ? '15-minute Triage Call' : '45-minute Admissions Call'}
                  </span>
                  <h1>{isRescheduleMode ? 'Choose your new time' : 'Choose your time'}</h1>
                  <p>
                    {isRescheduleMode
                      ? 'Pick a new date and time for this booking. The meeting will stay with your assigned admissions advisor.'
                      : `Pick a date and a start time. We’ll assign an available admissions advisor and book a ${sessionMinutes}-minute Google Meet as soon as you select it.`}
                  </p>
                  <div className={styles.timezoneBanner} role="note">
                    <Icon name="schedule" className={styles.timezoneBannerIcon} />
                    <p className={styles.timezoneBannerTitle}>
                      {timezoneDisplay(timezone).title}
                    </p>
                  </div>
                  {isRescheduleMode && rescheduleBooking?.startIso ? (
                    <p className={styles.rescheduleHint}>
                      Current booking: {formatDateWithZone(rescheduleBooking.startIso, timezone || 'America/New_York', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                      })}{' '}
                      at{' '}
                      {formatTimeRange(
                        rescheduleBooking.startIso,
                        rescheduleBooking.endIso,
                        timezone || 'America/New_York'
                      )}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className={styles.scheduleGrid}>
                <section className={styles.card}>
                  <div className={styles.calendarHeader}>
                    <h3>
                      {new Intl.DateTimeFormat('en-US', {
                        month: 'long',
                        year: 'numeric',
                      }).format(visibleMonth)}
                    </h3>
                    <div className={styles.calendarActions}>
                      <button
                        type="button"
                        className={styles.iconButtonSoft}
                        onClick={() => setVisibleMonth((current) => shiftMonth(current, -1))}
                        aria-label="Previous month"
                      >
                        <Icon name="chevron_left" />
                      </button>
                      <button
                        type="button"
                        className={styles.iconButtonSoft}
                        onClick={() => setVisibleMonth((current) => shiftMonth(current, 1))}
                        aria-label="Next month"
                      >
                        <Icon name="chevron_right" />
                      </button>
                    </div>
                  </div>

                  <div className={styles.calendarWeekdays}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <span key={day}>{day}</span>
                    ))}
                  </div>

                  <div className={styles.calendarGrid}>
                    {calendarDays.map((day) => (
                      <button
                        key={day.key}
                        type="button"
                        className={cx(
                          styles.calendarDay,
                          !day.inMonth && styles.calendarDayMuted,
                          day.disabled && styles.calendarDayDisabled,
                          day.isSelected && styles.calendarDaySelected,
                          day.isToday && styles.calendarDayToday
                        )}
                        onClick={() => {
                          if (!day.disabled) setDateStr(day.ymd);
                        }}
                        disabled={day.disabled}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </section>

                <section className={styles.card}>
                  <div className={styles.slotsHeader}>
                    <div>
                      <h3>
                        Available Slots —{' '}
                        {formatDate(dateStr, { month: 'short', day: 'numeric' })}
                      </h3>
                    </div>
                    <span className={styles.timezoneBadge}>
                      {timezoneDisplay(timezone).badge}
                    </span>
                  </div>

                  {loadingSlots ? (
                    <div className={styles.emptyState}>Loading available slots…</div>
                  ) : slots.length === 0 ? (
                    <div className={styles.emptyState}>
                      {`No open ${sessionMinutes}-minute slots on this date. Try another date. We also hold a ${bufferMinutes}-minute buffer before and after bookings.`}
                    </div>
                  ) : (
                    <div className={styles.slotGroups}>
                      {slotGroups.map(([groupLabel, groupSlots]) => (
                        <div key={groupLabel}>
                          <p className={styles.slotGroupLabel}>{groupLabel}</p>
                          <div className={styles.slotGrid}>
                            {groupSlots.map((slot) => (
                              <button
                                key={slot.start}
                                type="button"
                                className={cx(
                                  styles.slotButton,
                                  slotStart === slot.start && styles.slotButtonActive
                                )}
                                onClick={() => setSlotStart(slot.start)}
                              >
                                {slot.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              {TURNSTILE_SITE_KEY && !isRescheduleMode ? (
                <div className={styles.turnstileWrap}>
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken('')}
                    onError={() => setTurnstileToken('')}
                  />
                </div>
              ) : null}

              <div className={styles.previewBar}>
                <div className={styles.previewContent}>
                  <div className={styles.previewIcon}>
                    <Icon name="confirmation_number" />
                  </div>
                  <div>
                    <p className={styles.previewLabel}>Session Preview</p>
                    <p className={styles.previewText}>
                      {previewSummary || 'Choose a time to preview your appointment.'}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className={styles.previewButton}
                  onClick={goFromScheduleToReview}
                  disabled={!canBookSlot || submitLoading}
                >
                  {submitLoading ? 'Booking…' : 'Book this time'}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {currentStep === STEP_CONTACT ? (
          <section className={cx(styles.detailsPage, styles.contactPage, styles.contactLanding)}>
            <header className={styles.heroHeader}>
              <span className={styles.sectionKicker}>Office of Admissions</span>
              <h1>{`Let\u2019s get started`}</h1>
              <div className={styles.ornament} aria-hidden="true">
                <span className={styles.ornamentDot} />
              </div>
              <p>
                Start with your name, email, and phone. We save those first, then ask three questions so we can figure out how best to help you.
              </p>
            </header>

            <section className={cx(styles.card, styles.formCard, styles.contactLandingCard)}>
              <div className={styles.detailsForm}>
                <div className={styles.fieldGrid}>
                  <div className={styles.fieldGroup}>
                    <label htmlFor="booking-name">Full Name</label>
                    <input
                      id="booking-name"
                      className={styles.input}
                      placeholder="Enter your full name"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      autoComplete="name"
                    />
                  </div>

                  <div className={styles.fieldGroup}>
                    <label htmlFor="booking-email">Email Address</label>
                    <input
                      id="booking-email"
                      className={styles.input}
                      type="email"
                      placeholder="name@school.edu"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className={styles.fieldGroup}>
                  <label htmlFor="booking-phone">Phone Number</label>
                  <input
                    id="booking-phone"
                    className={styles.input}
                    type="tel"
                    placeholder="Best number for reminders or follow-up"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    autoComplete="tel"
                    maxLength={32}
                  />
                </div>

                <div className={styles.formActionsRow}>
                  <span className={styles.contactLandingHint}>
                    Next, you’ll answer three questions so we can figure out how best to help you.
                  </span>
                  <button
                    type="button"
                    className={styles.ctaButton}
                    onClick={goToProvidersFromContact}
                    disabled={!canContinueContact || submitLoading}
                  >
                    <span>Continue</span>
                    <Icon name="arrow_forward" className={styles.inlineIcon} />
                  </button>
                </div>
              </div>
            </section>
          </section>
        ) : null}

        {currentStep === STEP_DETAILS ? (
          <section className={styles.detailsPage}>
            <header className={styles.heroHeader}>
              <span className={styles.sectionKicker}>Qualification</span>
              <h1>Three questions</h1>
              <p>
                {isRescheduleMode
                  ? `Confirm the new time for your booking with ${assignedFirstName}. Submitting updates your existing session.`
                  : 'Answer these questions so we can figure out how best to help you.'}
              </p>
            </header>

            <div className={styles.detailsGrid}>
              <section className={cx(styles.card, styles.formCard)}>
                <form className={styles.detailsForm} onSubmit={handleSaveIntake} autoComplete="on">
                  {isRescheduleMode ? (
                    <div className={styles.intakeWizard}>
                      <p className={styles.intakeRescheduleNote}>
                        Your booking stays with {assignedFirstName}. Choose confirm to update the time.
                      </p>
                    </div>
                  ) : (
                    <div className={styles.intakeWizard}>
                      <div className={styles.intakeProgress}>
                        <p className={styles.intakeProgressLabel}>
                          Question {intakeStep + 1} of {INTAKE_QUESTIONS.length}
                        </p>
                        <div className={styles.intakeDots} role="tablist" aria-label="Intake questions">
                          {INTAKE_QUESTIONS.map((question, index) => {
                            const answered = isIntakeAnswerComplete(
                              question,
                              intakeAnswers[question.key]
                            );
                            const reachable =
                              index <= intakeStep ||
                              INTAKE_QUESTIONS.slice(0, index).every((item) =>
                                isIntakeAnswerComplete(item, intakeAnswers[item.key])
                              );
                            return (
                              <button
                                key={question.key}
                                type="button"
                                role="tab"
                                aria-selected={index === intakeStep}
                                aria-label={`${question.shortLabel}${answered ? ', answered' : ''}`}
                                className={cx(
                                  styles.intakeDot,
                                  index === intakeStep && styles.intakeDotActive,
                                  answered && styles.intakeDotDone
                                )}
                                disabled={!reachable}
                                onClick={() => reachable && setIntakeStep(index)}
                              />
                            );
                          })}
                        </div>
                      </div>

                      <fieldset className={styles.intakeQuestion}>
                        <legend>{currentIntakeQuestion.label}</legend>
                        {currentIntakeQuestion.helper ? (
                          <p className={styles.intakeHelper}>{currentIntakeQuestion.helper}</p>
                        ) : null}

                        {currentIntakeQuestion.type === 'text' ? (
                          <div className={styles.intakeTextWrap}>
                            <textarea
                              id={`intake-${currentIntakeQuestion.key}`}
                              className={styles.intakeTextarea}
                              placeholder={currentIntakeQuestion.placeholder}
                              maxLength={currentIntakeQuestion.maxLength}
                              value={currentIntakeValue}
                              onChange={(event) =>
                                setIntakeAnswers((current) => ({
                                  ...current,
                                  [currentIntakeQuestion.key]: event.target.value,
                                }))
                              }
                              required
                            />
                            <div className={styles.intakeTextMeta}>
                              <span>
                                {currentIntakeComplete
                                  ? 'Looks good'
                                  : `At least ${currentIntakeQuestion.minLength} characters`}
                              </span>
                              <span>
                                {String(currentIntakeValue).trim().length}
                                {currentIntakeQuestion.maxLength
                                  ? ` / ${currentIntakeQuestion.maxLength}`
                                  : ''}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.intakeOptions} role="radiogroup">
                            {currentIntakeQuestion.options.map((option) => {
                              const checked = currentIntakeValue === option;
                              const optionId = `${currentIntakeQuestion.key}-${option
                                .replace(/[^a-z0-9]+/gi, '-')
                                .toLowerCase()}`;
                              return (
                                <label
                                  key={option}
                                  htmlFor={optionId}
                                  className={cx(
                                    styles.intakeOption,
                                    checked && styles.intakeOptionSelected
                                  )}
                                >
                                  <input
                                    id={optionId}
                                    type="radio"
                                    name={currentIntakeQuestion.key}
                                    value={option}
                                    checked={checked}
                                    onChange={() =>
                                      selectIntakeOption(currentIntakeQuestion, option)
                                    }
                                  />
                                  <span className={styles.intakeOptionMark} aria-hidden="true">
                                    {checked ? <Icon name="check" /> : null}
                                  </span>
                                  <span>{option}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </fieldset>
                    </div>
                  )}

                  <div className={styles.formActions}>
                    <div className={styles.formActionsRow}>
                      <button
                        type="button"
                        className={styles.textAction}
                        onClick={
                          isRescheduleMode
                            ? () => setCurrentStep(STEP_SCHEDULE)
                            : lastBooking && intakeStep === 0
                              ? () => setCurrentStep(STEP_CONFIRMATION)
                              : goToPreviousIntakeStep
                        }
                      >
                        {isRescheduleMode
                          ? 'Back to schedule'
                          : intakeStep === 0
                            ? 'Back to contact'
                            : 'Back'}
                      </button>
                      {isRescheduleMode || isLastIntakeStep ? (
                        <button
                          type="submit"
                          className={styles.ctaButton}
                          disabled={submitLoading || (!isRescheduleMode && isLastIntakeStep && !intakeComplete && !lastBooking)}
                        >
                          <span>
                            {submitLoading
                              ? 'Saving…'
                              : isRescheduleMode
                                ? 'Confirm new time'
                                : isLastIntakeStep
                                  ? 'See next step'
                                  : 'Continue'}
                          </span>
                          <Icon name="arrow_forward" className={styles.inlineIcon} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={styles.ctaButton}
                          onClick={goToNextIntakeStep}
                          disabled={!currentIntakeComplete}
                        >
                          <span>Continue</span>
                          <Icon name="arrow_forward" className={styles.inlineIcon} />
                        </button>
                      )}
                    </div>
                    {isRescheduleMode ? (
                      <p className={styles.helperText}>
                        By booking, you agree to our terms of service and session cancellation
                        policy.
                      </p>
                    ) : null}
                  </div>
                </form>
              </section>

              <aside className={cx(styles.card, styles.summaryCard)}>
                <div className={styles.summaryHead}>
                  {isRescheduleMode ? (
                    <Avatar
                      label={assignedProvider?.name || 'Admissions Advisor'}
                      square
                      large
                      src={providerPhoto(assignedProvider)}
                    />
                  ) : (
                    <TeamAvatars people={providers} large />
                  )}
                  <div>
                    <p className={styles.summaryEyebrow}>Your Admissions Advisor</p>
                    <h3>
                      {isRescheduleMode
                        ? assignedProvider?.name || 'Admissions Advisor'
                        : 'Matched at confirmation'}
                    </h3>
                    <p className={styles.summaryRole}>
                      {isRescheduleMode
                        ? 'Admissions Advisor'
                        : lastBooking?.providerName || 'Assigned when you booked'}
                    </p>
                  </div>
                </div>

                <div className={styles.summaryList}>
                  <div className={styles.summaryItem}>
                    <div className={styles.summaryIconWrap}>
                      <Icon name="calendar_today" />
                    </div>
                    <div className={styles.summaryItemBody}>
                      <span className={styles.summaryItemLabel}>Date</span>
                      <strong className={styles.summaryItemValue}>
                        {formatDate(dateStr, {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </strong>
                    </div>
                  </div>

                  <div className={styles.summaryItem}>
                    <div className={styles.summaryIconWrap}>
                      <Icon name="schedule" />
                    </div>
                    <div className={styles.summaryItemBody}>
                      <span className={styles.summaryItemLabel}>Time</span>
                      <strong className={styles.summaryItemValue}>
                        {slotStart && selectedSlotEnd
                          ? `${formatTimeRange(slotStart, selectedSlotEnd, timezone)} · ${sessionMinutes} min`
                          : 'Choose a slot in the previous step'}
                      </strong>
                    </div>
                  </div>

                  <div className={styles.summaryItem}>
                    <div className={styles.summaryIconWrap}>
                      <Icon name="video_chat" />
                    </div>
                    <div className={styles.summaryItemBody}>
                      <span className={styles.summaryItemLabel}>Location</span>
                      <strong className={styles.summaryItemValue}>
                        Google Meet (link in your confirmation email)
                      </strong>
                    </div>
                  </div>
                </div>

                <div className={styles.summaryDivider} />

                <div className={styles.priceRows}>
                  <div className={styles.priceRow}>
                    <span>Call fee</span>
                    <span>Complimentary for prospective residents</span>
                  </div>
                  <div className={cx(styles.priceRow, styles.priceTotal)}>
                    <span>Total</span>
                    <span>$0.00</span>
                  </div>
                </div>

                <div className={styles.quoteCard}>
                  {isRescheduleMode
                    ? 'We will update your existing booking and send a fresh confirmation email with your new time.'
                    : 'Your answers help us understand how to support you from here.'}
                </div>
              </aside>
            </div>
          </section>
        ) : null}

        {currentStep === STEP_CONFIRMATION && !lastBooking && qualification?.route === 'future_follow_up' ? (
          <section className={styles.confirmPage}>
            <div className={styles.confirmHero}>
              <div className={styles.confirmHeroInner}>
                <div className={styles.successIcon}>
                  <Icon name="check_circle" filled />
                </div>
                <span className={styles.sectionKicker}>Saved for follow-up</span>
                <h1>We’ll follow up with you</h1>
                <p className={styles.confirmHeroSub}>
                  We saved your details. An admissions teammate will follow up with the best next step.
                  {qualification.suggestSociety ? (
                    <>
                      {' '}
                      TradeIT Society may be a helpful option while you plan for the Residency.
                    </>
                  ) : null}
                </p>
                {qualification.suggestSociety && societyUrl ? (
                  <p>
                    <a href={societyUrl} target="_blank" rel="noreferrer">
                      Learn about TradeIT Society
                    </a>
                  </p>
                ) : null}
              </div>
            </div>
            <div className={styles.confirmGrid}>
              <section className={cx(styles.card, styles.confirmMain)}>
                <p><strong>Name:</strong> {name}</p>
                <p><strong>Email:</strong> {email}</p>
                <p><strong>Phone:</strong> {phone}</p>
                {utm.utm_source || utm.utm_campaign ? (
                  <p>
                    <strong>How you found us:</strong>{' '}
                    {[utm.utm_source, utm.utm_campaign].filter(Boolean).join(' · ')}
                  </p>
                ) : null}
              </section>
            </div>
          </section>
        ) : null}

        {currentStep === STEP_CONFIRMATION && lastBooking ? (
          <section className={styles.confirmPage}>
            <div className={styles.confirmHero}>
              <div className={styles.confirmHeroInner}>
                <div className={styles.successIcon}>
                  <Icon name="check_circle" filled />
                </div>
                <span className={styles.sectionKicker}>You’re booked</span>
                <h1>Booking confirmed</h1>
                <p className={styles.confirmHeroSub}>
                  {lastBooking.wasRescheduled ? (
                    <>
                      {`Your `}
                      <strong>Admissions Advisor Booking</strong>
                      {` has been rescheduled. We\u2019ve emailed your updated confirmation and Google Meet details.`}
                    </>
                  ) : (
                    <>
                      {`Your `}
                      <strong>{lastBooking.durationMinutes || sessionMinutes}-minute Admissions Advisor Booking</strong>
                      {` is saved. We\u2019ve emailed a confirmation and your Google Meet link to the address you provided\u2014check your inbox (and spam) before your residency insight call.`}
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className={styles.confirmGrid}>
              <section className={cx(styles.card, styles.confirmMain)}>
                <div className={styles.confirmTop}>
                  <div className={styles.confirmAdvisor}>
                    <Avatar
                      label={lastBooking.providerName}
                      src={providerPhoto({ id: lastBooking.providerId })}
                    />
                    <div>
                      <span>Admissions Advisor</span>
                      <h3>{lastBooking.providerName}</h3>
                      <p>{lastBooking.providerRole}</p>
                    </div>
                  </div>

                  <div className={styles.confirmSessionType}>
                    <span>Call focus</span>
                    <strong>{lastBooking.sessionType}</strong>
                  </div>
                </div>

                <div className={styles.confirmMeta}>
                  <div className={styles.confirmMetaItem}>
                    <div className={styles.summaryIconWrap}>
                      <Icon name="calendar_today" />
                    </div>
                    <div className={styles.confirmMetaBody}>
                      <span className={styles.confirmMetaLabel}>Date</span>
                      <strong className={styles.confirmMetaValue}>
                        {formatDateWithZone(lastBooking.startIso, lastBooking.timezone, {
                          weekday: 'long',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </strong>
                    </div>
                  </div>

                  <div className={styles.confirmMetaItem}>
                    <div className={styles.summaryIconWrap}>
                      <Icon name="schedule" />
                    </div>
                    <div className={styles.confirmMetaBody}>
                      <span className={styles.confirmMetaLabel}>Time</span>
                      <strong className={styles.confirmMetaValue}>
                        {formatTimeRange(
                          lastBooking.startIso,
                          lastBooking.endIso,
                          lastBooking.timezone
                        )}
                      </strong>
                      <p className={styles.confirmMetaZone}>
                        {timezoneDisplay(lastBooking.timezone).confirm}
                        {lastBooking.timezone ? ` · ${lastBooking.timezone}` : ' · America/New_York'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className={styles.meetCard}>
                  <div className={styles.meetCopy}>
                    <div className={styles.meetBadge}>
                      <Icon name="videocam" />
                    </div>
                    <div>
                      <p>Google Meet Link</p>
                      <span>{lastBooking.meetLink}</span>
                    </div>
                  </div>

                  <div className={styles.meetActions}>
                    <button type="button" className={styles.ghostButton} onClick={copyMeetLink}>
                      {copiedMeet ? 'Copied!' : 'Copy'}
                    </button>
                    <a
                      className={styles.ctaButtonSmall}
                      href={lastBooking.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon name="videocam" className={styles.inlineIcon} />
                      <span>Open Meet</span>
                    </a>
                  </div>
                </div>
                {lastBooking.rescheduleUrl ? (
                  <a className={styles.rescheduleCta} href={lastBooking.rescheduleUrl}>
                    <Icon name="event_repeat" className={styles.inlineIcon} />
                    <span>Change time</span>
                  </a>
                ) : null}
              </section>

              <aside className={styles.confirmSide}>
                <section className={cx(styles.card, styles.sideCard)}>
                  <h4>Add to Calendar</h4>
                  <div className={styles.sideActions}>
                    {calendarAddUrl ? (
                      <a
                        className={styles.calendarAction}
                        href={calendarAddUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon name="event" />
                        <span>Google Calendar</span>
                      </a>
                    ) : null}

                    {icsHref ? (
                      <a className={styles.calendarAction} href={icsHref} download="admissions-advisor-booking.ics">
                        <Icon name="calendar_month" />
                        <span>Outlook/iCalendar</span>
                      </a>
                    ) : null}
                  </div>
                </section>

                <section className={cx(styles.card, styles.tipCard)}>
                  <h4>
                    <Icon name="lightbulb" className={styles.inlineIcon} />
                    Before your admissions advisor call
                  </h4>
                  <ul className={styles.tipList}>
                    {lastBooking.quickTips.map((tip) => (
                      <li key={tip}>
                        <Icon name="check" className={styles.smallIcon} />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <div className={styles.confirmActions}>
                  <p>Need to make a change?</p>
                  <div>
                    <button type="button" className={styles.textAction} onClick={() => setCurrentStep(STEP_SCHEDULE)}>
                      Reschedule
                    </button>
                    <span>|</span>
                    <button type="button" className={styles.dangerAction} onClick={resetToLanding}>
                      Book Another Session
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          </section>
        ) : null}
      </main>

      <footer className={styles.siteFoot}>
        {`© ${new Date().getFullYear()} TradeIT · Accelerated Residency`}
      </footer>

      <nav className={styles.mobileNav} aria-label="Mobile navigation">
        <button type="button" className={styles.mobileNavItem} onClick={resetToProviders}>
          <Icon name="groups" />
          <span>Questions</span>
        </button>
        <button
          type="button"
          className={styles.mobileNavItem}
          onClick={goToScheduleFromNav}
        >
          <Icon name="event_available" />
          <span>Schedule</span>
        </button>
        <button type="button" className={styles.mobileNavItem} onClick={goToSessions}>
          <Icon name="confirmation_number" />
          <span>My Booking</span>
        </button>
      </nav>
    </div>
  );
}

export default App;
